/**
 * Pure helpers used by the AudioEngine. Extracted into their own module so
 * they can be unit-tested without standing up the full Web Codecs +
 * AudioContext stack that the engine itself requires.
 *
 * - KbpsTracker: 1-second sliding window of byte counters, used to drive the
 *   "X.X kbps (last 1s)" line in the self-diagnostics card.
 * - lineariseRing: turn a ring buffer (data, writeIndex, filled) into a
 *   chronologically-ordered Float32Array suitable for export.
 * - writeRing: append samples to a fixed-size ring buffer with wrap.
 */

export interface RingState {
  writeIndex: number
  filled: boolean
}

/**
 * Append `samples` into the ring buffer. Mutates `state.writeIndex` and
 * `state.filled` in place. Caller owns the buffer.
 */
export function writeRing(buffer: Float32Array, state: RingState, samples: Float32Array): void {
  const cap = buffer.length
  let w = state.writeIndex
  for (let i = 0; i < samples.length; i++) {
    buffer[w] = samples[i]!
    w += 1
    if (w === cap) { w = 0; state.filled = true }
  }
  state.writeIndex = w
}

/**
 * Return a fresh Float32Array holding the ring's samples in chronological
 * order: oldest first, newest last. If the ring hasn't wrapped yet the
 * result is shorter than the buffer (samples 0..writeIndex). Once filled,
 * the result is the same length as the buffer.
 */
export function lineariseRing(ring: Float32Array, writeIndex: number, filled: boolean): Float32Array {
  if (!filled) {
    return ring.slice(0, writeIndex)
  }
  const out = new Float32Array(ring.length)
  out.set(ring.subarray(writeIndex), 0)
  out.set(ring.subarray(0, writeIndex), ring.length - writeIndex)
  return out
}

/**
 * Sliding-window byte counter, returns kbps over the last `windowMs`. We
 * use this to drive the "X.X kbps" stats line so brief PTT bursts aren't
 * washed out by a long-term average.
 *
 * `now` is injected so tests can advance time deterministically.
 */
export class KbpsTracker {
  private readonly events: Array<{ ms: number; bytes: number }> = []
  private totalBytes = 0
  private packets = 0
  private readonly windowMs: number
  private readonly now: () => number

  constructor(windowMs = 1000, now: () => number = Date.now) {
    this.windowMs = windowMs
    this.now = now
  }

  record(bytes: number): void {
    this.totalBytes += bytes
    this.packets += 1
    const t = this.now()
    this.events.push({ ms: t, bytes })
    this.prune(t)
  }

  /** Current windowed throughput, in kbps. */
  kbps(): number {
    this.prune(this.now())
    let bytes = 0
    for (const e of this.events) bytes += e.bytes
    return (bytes * 8) / 1000
  }

  /** Lifetime counters. */
  totals(): { packets: number; bytes: number } {
    return { packets: this.packets, bytes: this.totalBytes }
  }

  private prune(now: number): void {
    const cutoff = now - this.windowMs
    while (this.events.length > 0 && this.events[0]!.ms < cutoff) {
      this.events.shift()
    }
  }
}
